import React, {
  memo,
  createRef,
  MouseEvent,
  KeyboardEvent,
  ReactElement,
  useState,
  useEffect,
} from 'react'
import classNames from 'classnames'
import 'scss/5_components/_dpl-functional-tag.scss'
// eslint-disable-next-line perfectionist/sort-imports
import Spinner from 'transfix-ui/components/Spinner'
import { TAG_SIZE } from 'transfix-ui/components/Tag'
import { Box, makeStyles } from 'transfix-ui/core'
import { brandColors } from 'transfix-ui/theme'
import uuid from 'uuid/v4'
import { DownArrowIcon } from 'transfix-icons'
import { useDropdownPosition } from 'utils/dropdownPosition'
import { useToggle } from 'utils/useToggle'
import { FunctionalTagOption } from './components'
import { IFunctionalTagOption, TOptionValue } from './types'

export enum FUNCTIONAL_TAG_STYLE {
  success = 'success',
  error = 'error',
  info = 'info',
}

const useFunctionalTagStyles = makeStyles(() => ({
  container: {
    width: '220px !important',
  },
  itemContainer: {
    '& .dpl-functional-tag-listbox-item': {
      '&:hover': {
        backgroundColor: 'transparent',
      },
    },
    '&:hover': {
      backgroundColor: brandColors.coolGray1,
    },
    padding: '4px 0',
    margin: '1px 0',
  },
  disabledItemContainer: {
    backgroundColor: brandColors.coolGray3,
    color: brandColors.white,
    cursor: 'no-drop',
    '&:hover': {
      backgroundColor: brandColors.coolGray3,
    },
  },
  item: {
    border: 'none !important',
    width: '100% !important',
    textAlign: 'left',
  },
  description: {
    padding: '0 10px',
    color: brandColors.gray3,
  },
}))

export interface IFunctionalTagProps<T extends TOptionValue = string> {
  id?: string
  size?: TAG_SIZE
  value: T
  options: IFunctionalTagOption<T>[]
  isLoading?: boolean
  dataTest?: string
  onSelect?: (selectedOption: IFunctionalTagOption<T>) => void
  /**
   * @default default
   */
  renderType?: 'default' | 'withDescription'
}

export const FunctionalTag = memo<IFunctionalTagProps>(
  ({
    id,
    size = TAG_SIZE.medium,
    value,
    options,
    isLoading,
    onSelect,
    dataTest = 'functional-tag',
    renderType = 'default',
  }) => {
    const styles = useFunctionalTagStyles()
    const [expanded, toggle, wrapperRef] = useToggle<HTMLInputElement>(false)
    const [selectedOption, setSelectedOption] = useState<IFunctionalTagOption>(
      options.find(o => o.value === value) || options[0]
    )

    useEffect(() => {
      setSelectedOption(options.find(o => o.value === value) || options[0])
    }, [value, options])

    const close = () => {
      if (expanded) toggle()
    }

    const optionListRef = createRef<HTMLDivElement>()
    const [targetRef, targetStyle] = useDropdownPosition(expanded, close, wrapperRef, false, true)

    const triggerClickHandler = (e: MouseEvent<HTMLElement>) => {
      e.stopPropagation()
      toggle()
    }

    const keyPressHandler = (e: KeyboardEvent<HTMLElement>) => {
      e.stopPropagation()
      toggle()
    }

    const optionSelectedHandler = (option: IFunctionalTagOption) => {
      if (onSelect) {
        onSelect(option)
      }
      toggle()
    }

    const triggerClasses = [
      'dpl-functional-tag-trigger',
      `dpl-functional-tag-trigger-${size}`,
      `dpl-functional-tag-trigger-${selectedOption?.style || 'info'}`,
      selectedOption?.outlineOnly && 'dpl-functional-tag-trigger-outline',
    ]
      .filter(Boolean)
      .join(' ')

    const renderOptions = (): ReactElement[] => {
      if (renderType === 'withDescription') {
        return options.map(option => (
          <Box
            key={`dpl-functional-tag-listbox-item-${option.value}-${uuid()}`}
            className={classNames(styles.itemContainer, {
              [styles.disabledItemContainer]: option.disabled,
            })}
            onClick={e => {
              if (option.disabled) return

              e.stopPropagation()

              optionSelectedHandler(option)
            }}>
            <FunctionalTagOption
              itemClassNames={styles.item}
              size={size}
              option={option}
              onOptionSelected={optionSelectedHandler}
              dataTest={`${dataTest}-listbox-${option.value}`}
            />
            <div className={styles.description}>{option.description}</div>
          </Box>
        ))
      }

      return options.map(option => (
        <FunctionalTagOption
          key={`dpl-functional-tag-listbox-item-${option.value}-${uuid()}`}
          size={size}
          option={option}
          onOptionSelected={optionSelectedHandler}
          dataTest={`${dataTest}-listbox-${option.value}`}
        />
      ))
    }

    return (
      <span id={id} className='dpl-functional-tag' data-test={dataTest} ref={wrapperRef}>
        <span
          role='button'
          aria-haspopup='listbox'
          tabIndex={0}
          className={triggerClasses}
          onClick={triggerClickHandler}
          onKeyDown={keyPressHandler}
          ref={targetRef}>
          <span className='dpl-functional-tag-trigger-label' data-test={`${dataTest}-label`}>
            {selectedOption?.label || 'Invalid Value'}
          </span>
          {isLoading ? <Spinner position='relative' /> : <DownArrowIcon />}
        </span>
        {expanded && (
          <div
            role='listbox'
            style={targetStyle}
            className={`dpl-functional-tag-listbox ${styles.container}`}
            data-test={`${dataTest}-listbox`}
            ref={optionListRef}>
            {renderOptions()}
          </div>
        )}
      </span>
    )
  }
)

export default FunctionalTag
